import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserCircleIcon,
  TruckIcon,
  DocumentTextIcon,
  BellIcon,
  ShieldCheckIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const RiderProfile: React.FC = () => {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(true);
  const [editMode, setEditMode] = useState(false);

  const vehicleTypes = [
    'Bicycle',
    'Motorbike',
    'Sedan',
    'SUV',
    'Pickup Truck',
    'Medium Van',
    '12-Wheeler',
    '18-Wheeler',
    'Motorboat',
    'Light Plane',
    'Helicopter',
    'Cargo Plane'
  ];

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Profile & Settings
          </h1>
          <p className="mt-2 text-gray-600">Manage your profile, vehicle, and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6">
              <div className="text-center">
                <div className="mx-auto h-24 w-24 rounded-full bg-gradient-blue flex items-center justify-center mb-4">
                  <span className="text-white text-3xl font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">{user?.name}</h2>
                <p className="text-sm text-gray-600 mt-1">{user?.email}</p>
                <div className="mt-4">
                  <span className="px-3 py-1 bg-volexx-blue-100 text-volexx-blue-800 text-sm font-semibold rounded-volexx capitalize">
                    {user?.role}
                  </span>
                </div>
              </div>

              {/* Availability Toggle */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Availability Status</p>
                    <p className="text-sm text-gray-600">Accept new jobs</p>
                  </div>
                  <button
                    onClick={() => setIsOnline(!isOnline)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isOnline ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isOnline ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="mt-2">
                  <span className={`text-sm font-medium ${isOnline ? 'text-green-600' : 'text-gray-500'}`}>
                    {isOnline ? 'Online - Accepting Jobs' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Personal Information</h3>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="flex items-center text-volexx-blue-600 hover:text-volexx-blue-700 font-medium text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  {editMode ? 'Cancel' : 'Edit'}
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    defaultValue={user?.name}
                    disabled={!editMode}
                    className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    defaultValue={user?.email}
                    disabled={!editMode}
                    className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    defaultValue="+1 (555) 123-4567"
                    disabled={!editMode}
                    className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                {editMode && (
                  <button className="w-full bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white font-semibold py-2 rounded-volexx transition-colors">
                    Save Changes
                  </button>
                )}
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Vehicle Information</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent">
                    {vehicleTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
                    <input
                      type="text"
                      defaultValue="ABC-1234"
                      className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input
                      type="text"
                      defaultValue="Honda CRV"
                      className="w-full px-3 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Documents</h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-volexx">
                  <div className="flex items-center space-x-3">
                    <DocumentTextIcon className="h-5 w-5 text-volexx-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Driver's License</p>
                      <p className="text-xs text-gray-500">Expires: Dec 2025</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-volexx">
                    Verified
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-volexx">
                  <div className="flex items-center space-x-3">
                    <ShieldCheckIcon className="h-5 w-5 text-volexx-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Vehicle Insurance</p>
                      <p className="text-xs text-gray-500">Expires: Mar 2026</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-volexx">
                    Verified
                  </span>
                </div>
                <button className="w-full border-2 border-dashed border-gray-300 rounded-volexx py-3 text-gray-600 hover:border-volexx-blue-500 hover:text-volexx-blue-600 transition-colors">
                  + Upload Additional Documents
                </button>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Notification Preferences</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">New Job Alerts</p>
                    <p className="text-sm text-gray-600">Get notified when new jobs are available</p>
                  </div>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Payment Confirmations</p>
                    <p className="text-sm text-gray-600">Get notified when payments are processed</p>
                  </div>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Promotional Emails</p>
                    <p className="text-sm text-gray-600">Receive updates and promotional offers</p>
                  </div>
                  <input type="checkbox" className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiderProfile;
